import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Percent,
  Search,
  RefreshCcw,
  Save,
  RotateCcw,
  Trash2,
  ChevronDown,
  ChevronUp,
  Hash,
  User,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com";

const TDSMaster = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const userId = Number(localStorage.getItem("loginuserid") || localStorage.getItem("userId") || 0);

  // form
  const [showForm, setShowForm] = useState(true);
  const [editingId, setEditingId] = useState(null);

  const [startingSalary, setStartingSalary] = useState("");
  const [endingSalary, setEndingSalary] = useState("");
  const [percentage, setPercentage] = useState("");
  const [gender, setGender] = useState(""); // Male/Female

  // list
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-6", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const dangerBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ` +
    (selectedIds.length === 0 ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700");

  const resetForm = () => {
    setEditingId(null);
    setStartingSalary("");
    setEndingSalary("");
    setPercentage("");
    setGender("");
  };

  const validateForm = () => {
    if (!String(startingSalary).trim()) {
      toast.warn("Please Enter Starting Salary");
      return false;
    }
    if (!String(endingSalary).trim()) {
      toast.warn("Please Enter Ending Salary");
      return false;
    }
    if (!String(percentage).trim()) {
      toast.warn("Please Enter percentage");
      return false;
    }

    const s = Number(startingSalary);
    const e = Number(endingSalary);
    if (Number.isFinite(s) && Number.isFinite(e) && e < s) {
      toast.warn("Ending salary must be greater than or equal to starting salary");
      return false;
    }

    if (!gender) {
      toast.warn("Please select gender");
      return false;
    }
    return true;
  };

  const fetchList = async (p = page) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/tds-master/list`, {
        params: {
          finYearId: Number(finYearId),
          page: p,
          pageSize,
          search: search.trim(),
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);

        setSelectedIds([]);
        setSelectAll(false);
      } else {
        toast.error(res?.data?.message || "Failed to load TDS list.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!validateForm()) return;

    try {
      setLoading(true);

      const payload = {
        startingSalary: String(startingSalary).trim(),
        endingSalary: String(endingSalary).trim(),
        percentage: String(percentage).trim(),
        gender,
        userId,
      };

      if (editingId) {
        const res = await axios.put(`${BASE_URL}/api/tds-master/${editingId}`, payload, {
          params: { finYearId: Number(finYearId) },
        });

        if (res?.data?.success) {
          toast.success(res?.data?.message || "Updated Successfully..");
          resetForm();
          await fetchList(1);
          setPage(1);
        } else {
          toast.error(res?.data?.message || "Plz Retry..");
        }
      } else {
        const res = await axios.post(`${BASE_URL}/api/tds-master`, payload, {
          params: { finYearId: Number(finYearId) },
        });

        if (res?.data?.success) {
          toast.success(res?.data?.message || "Inserted Successfully..");
          resetForm();
          await fetchList(1);
          setPage(1);
        } else {
          toast.error(res?.data?.message || "Plz Retry..");
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  // ASP: click percentage link => View
  const handleViewEdit = async (id) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/tds-master/${id}`, {
        params: { finYearId: Number(finYearId) },
      });

      if (res?.data?.success && res?.data?.row) {
        const r = res.data.row;
        setEditingId(r.id);
        setStartingSalary(r.startingSalary ?? "");
        setEndingSalary(r.endingSalary ?? "");
        setPercentage(r.percentage ?? "");
        setGender(r.gender ?? "");
        setShowForm(true);
        toast.info("Loaded for editing");
      } else {
        toast.error(res?.data?.message || "Record not found.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
      return;
    }
    setSelectedIds(rows.map((r) => r.id));
    setSelectAll(true);
  };

  const handleDeleteSelected = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (selectedIds.length === 0) {
      toast.warn("Please select any one");
      return;
    }

    const ok = window.confirm("Are you sure to delete?");
    if (!ok) return;

    try {
      setLoading(true);

      const res = await axios.post(
        `${BASE_URL}/api/tds-master/delete-many`,
        { ids: selectedIds },
        { params: { finYearId: Number(finYearId) } }
      );

      if (res?.data?.success) {
        toast.success(res?.data?.message || "Deleted Successfully");
      } else {
        // ASP message in failures
        toast.warn(res?.data?.message || "Cannot Be Deleted As The Employee Is In Use");
      }

      const nextPage = page > 1 && rows.length === selectedIds.length ? page - 1 : page;
      setPage(nextPage);
      await fetchList(nextPage);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">TDS MASTER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <Percent className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Define TDS
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Add / Edit / Delete / Search / Pagination
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {screenBadge}
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={() => fetchList(page)} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={() => setShowForm((v) => !v)} className={ghostBtn}>
                {showForm ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showForm ? "Hide Form" : "Show Form"}
              </button>
            </div>
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className={cardBase + " p-5 mb-6"}>
            <div className="flex flex-col md:flex-row md:items-end gap-4">
              <div className="flex-1">
                <label className={labelBase}>Search (Salary / Percentage / Gender)</label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Search className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Type to search..."
                    className={`pl-11 ${inputBase}`}
                  />
                </div>
              </div>

              <button type="submit" className={primaryBtn} disabled={loading}>
                <Search className="w-4 h-4" />
                Search
              </button>
            </div>
          </form>

          {/* Form */}
          {showForm && (
            <form onSubmit={handleSave} className={cardBase + " p-6 mb-6"}>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
                <div className="text-lg font-extrabold">
                  {editingId ? `Edit TDS (ID: ${editingId})` : "New TDS"}
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={resetForm} className={ghostBtn}>
                    <RotateCcw className="w-4 h-4" />
                    Reset
                  </button>
                  <button type="submit" className={primaryBtn} disabled={loading}>
                    <Save className="w-4 h-4" />
                    {editingId ? "Update" : "Submit"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelBase}>
                    Starting Salary <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-2 flex items-center gap-2">
                    <Hash className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <input
                      value={startingSalary}
                      onChange={(e) => setStartingSalary(e.target.value)}
                      placeholder="e.g. 0"
                      className={inputBase}
                      inputMode="decimal"
                    />
                  </div>
                </div>

                <div>
                  <label className={labelBase}>
                    Ending Salary <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-2 flex items-center gap-2">
                    <Hash className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <input
                      value={endingSalary}
                      onChange={(e) => setEndingSalary(e.target.value)}
                      placeholder="e.g. 250000"
                      className={inputBase}
                      inputMode="decimal"
                    />
                  </div>
                </div>

                <div>
                  <label className={labelBase}>
                    Percentage <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-2 flex items-center gap-2">
                    <Percent className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <input
                      value={percentage}
                      onChange={(e) => setPercentage(e.target.value)}
                      placeholder="e.g. 5"
                      className={inputBase}
                      maxLength={5}
                      inputMode="numeric"
                    />
                  </div>
                </div>

                <div>
                  <label className={labelBase}>
                    Gender <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-2 flex items-center gap-4">
                    <label className={`inline-flex items-center gap-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                      <input
                        type="radio"
                        name="gender"
                        checked={gender === "Male"}
                        onChange={() => setGender("Male")}
                      />
                      <User className="w-4 h-4" />
                      Male
                    </label>

                    <label className={`inline-flex items-center gap-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                      <input
                        type="radio"
                        name="gender"
                        checked={gender === "Female"}
                        onChange={() => setGender("Female")}
                      />
                      <User className="w-4 h-4" />
                      Female
                    </label>
                  </div>
                </div>
              </div>
            </form>
          )}

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Starting Salary</th>
                    <th className="px-4 py-4 text-center font-bold">Ending Salary</th>
                    <th className="px-4 py-4 text-center font-bold">Percentage</th>
                    <th className="px-4 py-4 text-center font-bold">Gender</th>
                    <th className="px-4 py-4 text-center font-bold">
                      <div className="flex items-center justify-center gap-2">
                        Select All
                        <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 6 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr
                        key={r.id}
                        className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}
                      >
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.startingSalary}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.endingSalary}
                        </td>

                        {/* ASP: Percentage is link button (View) */}
                        <td className="px-4 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => handleViewEdit(r.id)}
                            className={`font-extrabold underline ${isDark ? "text-blue-300" : "text-blue-700"}`}
                            title="Click to edit"
                          >
                            {r.percentage}
                          </button>
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.gender}
                        </td>

                        <td className="px-4 py-4 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(r.id)}
                            onChange={() => toggleSelect(r.id)}
                            className="w-4 h-4"
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom row */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {totalPages > 1 && (
                <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === 1
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Prev
                  </button>

                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      className={`px-4 py-2 text-sm font-bold transition ${
                        page === i + 1
                          ? `${primaryBtnClass} text-white`
                          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === totalPages
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}

              <button onClick={handleDeleteSelected} disabled={selectedIds.length === 0} className={dangerBtn}>
                <Trash2 className="w-4 h-4" />
                Delete Selected
              </button>
            </div>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default TDSMaster;
